<template>
  <div class="product-case-edit-page" :class="{ 'is-upload-step': !parts.length, 'has-uploaded-images': hasUploadedImages }" v-loading="pageLoading">
    <section v-if="!parts.length" class="publish-upload-card">
      <div class="publish-upload-head">
        <div class="publish-title-block">
          <h1>上传零件图片，<span>AI</span>自动解析</h1>
          <p>上传图片后，AI将自动识别并生成零件草稿</p>
        </div>
        <ol class="publish-flow-steps">
          <li class="is-active"><b>1</b><span>上传素材</span></li>
          <li><b>2</b><span>AI解析</span></li>
          <li><b>3</b><span>核对发布</span></li>
        </ol>
      </div>
      <initial-image-uploader :site-type="siteType" :mock-enabled="mockEnabled" :user-info="userInfo" @upload-change="handleUploadChange" @parsed="handleParsed" />
    </section>

    <template v-else>
      <header class="review-head">
        <div>
          <h1>核对零件信息</h1>
          <p>请核对零件信息，确认无误后发布</p>
        </div>
        <ol class="review-flow-steps">
          <li class="is-done"><b>✓</b><span>上传素材</span></li>
          <li class="is-done"><b>✓</b><span>AI解析</span></li>
          <li class="is-active"><b>3</b><span>核对发布</span></li>
        </ol>
      </header>

      <div class="review-shell">
        <div ref="partNavigatorSlot" class="part-navigator-slot">
          <part-navigator
            :parts="parts"
            :active-index="activeIndex"
            :style="partNavigatorStyle"
            @change="activeIndex = $event"
          />
        </div>

        <section class="review-panel" v-if="currentPart">
          <div class="panel-toolbar">
            <strong>零件信息</strong>
            <ai-status-pills :part="currentPart" />
          </div>

          <div v-if="isCurrentPartProcessing" class="part-processing-tip">
            <i class="el-icon-loading"></i>
            <span>当前零件正在 AI 解析中，完成后会自动解禁编辑</span>
          </div>

          <div v-if="currentPart.warnings && currentPart.warnings.length" class="part-warnings">
            <i class="el-icon-warning-outline"></i>
            <span v-for="item in currentPart.warnings" :key="item">{{ item }}</span>
          </div>

          <h2>零件图片 <em>*</em></h2>
          <product-image-manager
            :part="currentPart"
            :site-type="siteType"
            :mock-enabled="mockEnabled"
            :disabled="isCurrentPartProcessing"
            @change="syncCurrentPart"
            @replace="replaceCurrentPart"
          />

          <product-editor
            :part="currentPart"
            :site-type="siteType"
            :disabled="isCurrentPartProcessing"
            :polish-loading="polishLoading"
            :polish-field="polishField"
            :category-options="categoryOptions"
            :currency-options="currencyOptions"
            :unit-options="unitOptions"
            :date-unit-options="dateUnitOptions"
            @polish="polishCurrentPart"
          />
        </section>
      </div>

      <footer class="bottom-actions">
        <el-button v-if="canSaveDraft" :loading="saveLoading" @click="saveDraft">保存草稿</el-button>
        <el-button :disabled="activeIndex === 0" @click="goPrev">上一个</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handlePrimaryAction">{{ primaryButtonText }}</el-button>
      </footer>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import InitialImageUploader from './components/InitialImageUploader.vue';
import PartNavigator from './components/PartNavigator.vue';
import AiStatusPills from './components/AiStatusPills.vue';
import ProductImageManager from './components/ProductImageManager.vue';
import ProductEditor from './components/ProductEditor.vue';
import { batchSaveDraft, batchSubmitReview, getDictByTypes, getMainCategoryList, getProductCaseDetail } from '@/utils/api/product-case';
import { getParseTask, refinePart } from '@/utils/api/agent-hub-product-case';
import { AI_OPERATION, SITE_TYPE } from '@/utils/product-case/constants';
import {
  canSaveDraftForParts,
  extractRspData,
  isRequestSuccess,
  normalizePart,
  parseFaqJson,
  preferProcessedImages,
  preparePartForSave,
  stringifyFaqList,
  syncMainFlag,
  validatePartDraftRequired,
  validatePartFieldLengths,
  validatePartImages,
  validatePartPrice,
  validatePartRequired
} from '@/utils/product-case/model';
import { isMockMode, polishMockPart, saveMockParts } from '@/utils/product-case/mock';

export default {
  name: 'ProductCaseEdit',
  components: {
    InitialImageUploader,
    PartNavigator,
    AiStatusPills,
    ProductImageManager,
    ProductEditor
  },
  data() {
    return {
      mode: 'create',
      siteType: SITE_TYPE.CN,
      parts: [],
      activeIndex: 0,
      pageLoading: false,
      saveLoading: false,
      submitLoading: false,
      polishLoading: false,
      polishField: '',
      hasUploadedImages: false,
      categoryOptions: [],
      currencyOptions: [],
      unitOptions: [],
      dateUnitOptions: [],
      parseTaskId: '',
      parseTaskTimer: null,
      partNavigatorStyle: {},
      partNavigatorScrollParent: null,
      partNavigatorScrollTargets: [],
      partNavigatorScrollRaf: null
    };
  },
  computed: {
    ...mapState(['userInfo']),
    compId() {
      return this.userInfo.compId;
    },
    mockEnabled() {
      return isMockMode(this.$route);
    },
    currentPart() {
      return this.parts[this.activeIndex];
    },
    canSaveDraft() {
      return canSaveDraftForParts(this.parts);
    },
    primaryButtonText() {
      return this.activeIndex < this.parts.length - 1 ? '下一个' : '保存并提交零件';
    },
    isCurrentPartProcessing() {
      return !!this.currentPart && this.currentPart.parseStatus === 'processing';
    },
    hasProcessingPart() {
      return this.parts.some(part => part.parseStatus === 'processing');
    },
    isGoldMember() {
      const memberType = this.userInfo.userMemberInfoVo && Number(this.userInfo.userMemberInfoVo.memberType);
      return memberType === 1;
    },
    dictLanguage() {
      return 'zh';
    }
  },
  watch: {
    'userInfo.userMemberInfoVo.memberType'() {
      this.applyMemberSiteLimit(true);
    }
  },
  mounted() {
    this.bindPartNavigatorScroll();
  },
  beforeDestroy() {
    this.stopParseTaskPolling();
    this.unbindPartNavigatorScroll();
  },
  created() {
    this.mode = this.$route.query.mode || 'create';
    this.siteType = this.isGoldMember ? SITE_TYPE.CN : Number(this.$route.query.siteType || SITE_TYPE.CN);
    this.applyMemberSiteLimit(false);
    this.loadMainCategories();
    this.loadCommonDicts();
    if (this.mode === 'intlDraft') {
      this.hasUploadedImages = true;
      this.loadInternationalDraft();
    } else if (this.$route.query.id) {
      this.hasUploadedImages = true;
      this.loadDetail();
    }
  },
  methods: {
    applyMemberSiteLimit(reloadDicts) {
      if (!this.isGoldMember) return;
      const changed = this.siteType !== SITE_TYPE.CN;
      this.siteType = SITE_TYPE.CN;
      if (Number(this.$route.query.siteType) !== SITE_TYPE.CN) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            siteType: SITE_TYPE.CN
          }
        }).catch(() => {});
      }
      if (reloadDicts && changed) {
        this.loadMainCategories();
        this.loadCommonDicts();
      }
    },
    handleUploadChange(count) {
      this.hasUploadedImages = count > 0;
    },
    handleParsed(payload) {
      const parts = Array.isArray(payload) ? payload : ((payload || {}).parts || []);
      if (!parts.length) {
        this.$message.warning('AI暂未解析出零件信息，请重新上传图片');
        return;
      }
      this.parts = parts.map(item => this.normalizeParsedPart(item));
      this.parseTaskId = Array.isArray(payload) ? '' : ((payload || {}).taskId || '');
      this.activeIndex = 0;
      this.scrollToPageTop();
      this.$nextTick(this.bindPartNavigatorScroll);
      if (this.parseTaskId && (!(payload || {}).status || payload.status !== 'completed')) {
        this.startParseTaskPolling();
      }
    },
    normalizeParsedPart(part) {
      const normalized = normalizePart(part, this.siteType);
      if (normalized.parseStatus === 'processing') return normalized;
      return preferProcessedImages(normalized);
    },
    startParseTaskPolling() {
      this.stopParseTaskPolling();
      this.parseTaskTimer = setInterval(this.fetchParseTask, 2000);
      this.fetchParseTask();
    },
    stopParseTaskPolling() {
      if (this.parseTaskTimer) {
        clearInterval(this.parseTaskTimer);
        this.parseTaskTimer = null;
      }
    },
    bindPartNavigatorScroll() {
      this.$nextTick(() => {
        this.unbindPartNavigatorScroll();
        const scrollParent = this.findMainScrollParent();
        const scrollTargets = this.findScrollTargets();
        this.partNavigatorScrollParent = scrollParent;
        this.partNavigatorScrollTargets = scrollTargets;
        scrollTargets.forEach(target => {
          target.addEventListener('scroll', this.requestPartNavigatorUpdate, { passive: true });
        });
        window.addEventListener('resize', this.requestPartNavigatorUpdate);
        this.updatePartNavigatorFixed();
      });
    },
    unbindPartNavigatorScroll() {
      (this.partNavigatorScrollTargets || []).forEach(target => {
        target.removeEventListener('scroll', this.requestPartNavigatorUpdate);
      });
      this.partNavigatorScrollTargets = [];
      this.partNavigatorScrollParent = null;
      window.removeEventListener('resize', this.requestPartNavigatorUpdate);
      if (this.partNavigatorScrollRaf) {
        cancelAnimationFrame(this.partNavigatorScrollRaf);
        this.partNavigatorScrollRaf = null;
      }
    },
    findMainScrollParent() {
      return this.findScrollTargets()[0] || window;
    },
    findScrollTargets() {
      const targets = [];
      let parent = this.$el && this.$el.parentElement;
      while (parent && parent !== document.body) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') targets.push(parent);
        parent = parent.parentElement;
      }
      if (!targets.includes(window)) targets.push(window);
      return targets;
    },
    requestPartNavigatorUpdate() {
      if (this.partNavigatorScrollRaf) return;
      this.partNavigatorScrollRaf = requestAnimationFrame(() => {
        this.partNavigatorScrollRaf = null;
        this.updatePartNavigatorFixed();
      });
    },
    updatePartNavigatorFixed() {
      const slot = this.$refs.partNavigatorSlot;
      if (!slot || !this.parts.length) {
        this.partNavigatorStyle = {};
        return;
      }
      const topGap = 12;
      const scrollParentRect = this.partNavigatorScrollParent && this.partNavigatorScrollParent !== window
        ? this.partNavigatorScrollParent.getBoundingClientRect()
        : { top: 0 };
      const fixedTop = Math.max(topGap, scrollParentRect.top + topGap);
      const rect = slot.getBoundingClientRect();
      if (rect.top > fixedTop) {
        this.partNavigatorStyle = {};
        return;
      }
      this.partNavigatorStyle = {
        position: 'fixed',
        top: fixedTop + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px',
        maxHeight: 'calc(100vh - ' + (fixedTop + 84) + 'px)',
        zIndex: 4
      };
    },
    async fetchParseTask() {
      if (!this.parseTaskId || this.mockEnabled) return;
      try {
        const res = await getParseTask(this.parseTaskId);
        const data = extractRspData(res);
        this.mergeParseTaskParts(data.parts || []);
        if (data.status === 'completed') {
          this.stopParseTaskPolling();
        }
      } catch (error) {
        this.stopParseTaskPolling();
      }
    },
    mergeParseTaskParts(nextParts) {
      nextParts.map(item => this.normalizeParsedPart(item)).forEach(nextPart => {
        const index = this.parts.findIndex(item => item.clientPartId === nextPart.clientPartId);
        if (index === -1) {
          this.parts.push(nextPart);
          return;
        }
        if (this.parts[index].parseStatus === 'processing' && nextPart.parseStatus !== 'processing') {
          this.parts.splice(index, 1, nextPart);
          return;
        }
        if (nextPart.faqStatus && this.parts[index].faqStatus !== nextPart.faqStatus) {
          this.parts.splice(index, 1, {
            ...this.parts[index],
            faqStatus: nextPart.faqStatus,
            faqJson: nextPart.faqJson || this.parts[index].faqJson,
            faqList: nextPart.faqList && nextPart.faqList.length ? nextPart.faqList : this.parts[index].faqList
          });
        }
      });
    },
    async loadMainCategories() {
      if (this.mockEnabled) return;
      try {
        const res = await getMainCategoryList(this.dictLanguage);
        this.categoryOptions = (((res || {}).rspListData || {}).list || []).map(item => ({
          label: item.name,
          value: item.codeValue
        }));
      } catch (error) {
        this.categoryOptions = [];
      }
    },
    async loadCommonDicts() {
      if (this.mockEnabled) return;
      try {
        const res = await getDictByTypes(['currency', 'unit', 'dateunit'], this.dictLanguage);
        const data = extractRspData(res);
        const codeMap = data.codeMap || {};
        this.currencyOptions = this.toDictOptions(codeMap.currency);
        this.unitOptions = this.toDictOptions(codeMap.unit);
        this.dateUnitOptions = this.toDictOptions(codeMap.dateunit);
      } catch (error) {
        this.currencyOptions = [];
        this.unitOptions = [];
        this.dateUnitOptions = [];
      }
    },
    toDictOptions(list) {
      return (list || []).map(item => ({
        label: item.name,
        value: item.codeValue
      }));
    },
    loadInternationalDraft() {
      const raw = sessionStorage.getItem('product-case-intl-draft');
      if (!raw) return;
      try {
        const payload = JSON.parse(raw);
        const parts = Array.isArray(payload) ? payload : ((payload || {}).parts || []);
        this.parts = parts.map(item => this.normalizeParsedPart(item));
        this.parseTaskId = Array.isArray(payload) ? '' : ((payload || {}).taskId || '');
        this.siteType = SITE_TYPE.INTL;
        this.activeIndex = 0;
        this.scrollToPageTop();
        this.$nextTick(this.bindPartNavigatorScroll);
        if (this.parseTaskId && (!(payload || {}).status || payload.status !== 'completed')) {
          this.startParseTaskPolling();
        }
      } catch (error) {
        this.$message.error('国际站草稿读取失败');
      }
    },
    async loadDetail() {
      if (this.mockEnabled) return;
      this.pageLoading = true;
      try {
        const res = await getProductCaseDetail({
          compId: this.compId,
          productId: this.$route.query.id,
          siteType: this.siteType
        }, this.dictLanguage);
        const data = extractRspData(res);
        this.parts = (data.parts || []).map(item => this.normalizeSavedPart(item));
        this.activeIndex = 0;
        this.scrollToPageTop();
        this.$nextTick(this.bindPartNavigatorScroll);
      } finally {
        this.pageLoading = false;
      }
    },
    normalizeSavedPart(part) {
      const normalized = normalizePart(part, this.siteType);
      normalized.productPictureList = (normalized.productPictureList || []).map(image => ({
        ...image,
        isCurrentUpload: false
      }));
      return normalized;
    },
    replaceCurrentPart(part) {
      this.parts.splice(this.activeIndex, 1, syncMainFlag(normalizePart(part, this.siteType)));
    },
    syncCurrentPart() {
      if (!this.currentPart) return;
      syncMainFlag(this.currentPart);
    },
    async polishCurrentPart(payload) {
      if (!this.currentPart) return;
      const refineField = typeof payload === 'object' && payload ? payload.refineField : payload;
      const faqItemIndex = typeof payload === 'object' && payload ? payload.faqItemIndex : null;
      const polishFieldKey = refineField === 'faq' && faqItemIndex !== null && faqItemIndex !== undefined ? `faq:${faqItemIndex}` : (refineField || '');
      const imageError = validatePartImages(this.currentPart);
      if (imageError) {
        this.$message.warning(imageError);
        return;
      }
      this.polishLoading = true;
      this.polishField = polishFieldKey;
      try {
        if (this.mockEnabled) {
          this.applyPolishResult(polishMockPart(this.currentPart, this.siteType, refineField), refineField, faqItemIndex);
          this.$message.success('AI润色完成（mock）');
          return;
        }
        const requestPart = refineField === 'faq'
          ? { ...this.currentPart, faqJson: stringifyFaqList(this.currentPart.faqList || [], { keepEmpty: true }) }
          : this.currentPart;
        const res = await refinePart({
          operation: AI_OPERATION.POLISH_TEXT,
          siteType: this.siteType,
          regenerateImages: false,
          refineField,
          faqItemIndex,
          part: requestPart
        });
        const data = extractRspData(res);
        const nextPart = (data.parts || [])[0];
        if (nextPart) {
          this.applyPolishResult(nextPart, refineField, faqItemIndex);
          this.$message.success('AI润色完成');
        }
      } finally {
        this.polishLoading = false;
        this.polishField = '';
      }
    },
    applyPolishResult(nextPart, refineField, faqItemIndex) {
      if (!this.currentPart || !nextPart) return;
      const normalized = normalizePart(nextPart, this.siteType);
      const isInternational = this.siteType === SITE_TYPE.INTL;
      if (refineField === 'name') {
        this.$set(this.currentPart, isInternational ? 'enName' : 'cnName', isInternational ? normalized.enName : normalized.cnName);
        return;
      }
      if (refineField === 'keywords') {
        this.$set(this.currentPart, 'keywords', normalized.keywords || []);
        return;
      }
      if (refineField === 'content') {
        this.$set(this.currentPart, isInternational ? 'enContent' : 'cnContent', isInternational ? normalized.enContent : normalized.cnContent);
        return;
      }
      if (refineField === 'faq') {
        this.$set(this.currentPart, 'faqStatus', normalized.faqStatus || 'success');
        if (faqItemIndex !== null && faqItemIndex !== undefined) {
          const currentFaqList = Array.isArray(this.currentPart.faqList) ? [...this.currentPart.faqList] : [];
          const nextItem = (normalized.faqList || [])[faqItemIndex] || (normalized.faqList || [])[0];
          if (nextItem) {
            currentFaqList.splice(faqItemIndex, 1, nextItem);
            this.$set(this.currentPart, 'faqList', currentFaqList);
            this.$set(this.currentPart, 'faqJson', stringifyFaqList(currentFaqList));
          }
          return;
        }
        this.$set(this.currentPart, 'faqJson', normalized.faqJson || '');
        this.$set(this.currentPart, 'faqList', normalized.faqList || []);
        return;
      }
      ['cnName', 'enName', 'cnContent', 'enContent', 'keywords', 'faqJson', 'faqList'].forEach(field => {
        this.$set(this.currentPart, field, normalized[field]);
      });
    },
    goPrev() {
      if (this.activeIndex > 0) this.activeIndex -= 1;
    },
    handlePrimaryAction() {
      if (this.activeIndex < this.parts.length - 1) {
        this.activeIndex += 1;
        return;
      }
      this.submitReview();
    },
    validateParts(needRequired) {
      const processingIndex = this.parts.findIndex(part => part.parseStatus === 'processing');
      if (processingIndex > -1) {
        this.activeIndex = processingIndex;
        this.$message.warning('还有零件正在处理中，请稍后再保存或提交');
        return false;
      }
      for (let i = 0; i < this.parts.length; i += 1) {
        const part = this.parts[i];
        const error = validatePartFieldLengths(part, this.siteType)
          || validatePartPrice(part)
          || (needRequired ? validatePartRequired(part, this.siteType) : (validatePartDraftRequired(part) || validatePartImages(part)));
        if (error) {
          this.activeIndex = i;
          this.$message.warning(`第 ${i + 1} 个零件：${error}`);
          return false;
        }
      }
      return true;
    },
    async saveDraft() {
      if (!this.canSaveDraft) {
        this.$message.warning('已发布或审核中的零件不能保存草稿，请直接提交');
        return;
      }
      if (!this.validateParts(false)) return;
      this.saveLoading = true;
      try {
        if (this.mockEnabled) {
          saveMockParts(this.buildSaveParts(), this.siteType, false);
          this.$message.success('保存草稿成功（mock）');
          this.$router.push({ name: 'product-case-list', query: { siteType: this.siteType, mock: '1' } });
          return;
        }
        const res = await batchSaveDraft({
          compId: this.compId,
          siteType: this.siteType,
          parts: this.buildSaveParts()
        });
        if (!isRequestSuccess(res)) return;
        this.$message.success('保存草稿成功');
        this.$router.push({ name: 'product-case-list', query: { siteType: this.siteType } });
      } finally {
        this.saveLoading = false;
      }
    },
    async submitReview() {
      if (!this.validateParts(true)) return;
      const shouldContinue = await this.confirmSubmitWithoutFaq();
      if (!shouldContinue) return;
      this.submitLoading = true;
      try {
        if (this.mockEnabled) {
          this.mergeSavedResult(saveMockParts(this.buildSaveParts(), this.siteType, true));
          this.$message.success(this.submitSuccessMessage() + '（mock）');
          this.$router.push({ name: 'product-case-list', query: { siteType: this.siteType, mock: '1' } });
          return;
        }
        const res = await batchSubmitReview({
          compId: this.compId,
          siteType: this.siteType,
          parts: this.buildSaveParts()
        });
        if (!isRequestSuccess(res)) return;
        this.$message.success(this.submitSuccessMessage());
        this.$router.push({ name: 'product-case-list', query: { siteType: this.siteType } });
      } finally {
        this.submitLoading = false;
      }
    },
    submitSuccessMessage() {
      return '提交成功，已发布';
    },
    buildSaveParts() {
      return this.parts.map(preparePartForSave);
    },
    async confirmSubmitWithoutFaq() {
      if (!this.hasMissingFaqPart()) return true;
      try {
        await this.$confirm('部分零件的常见咨询问题（FAQ）尚未生成。提交后，系统将停止继续生成FAQ。是否继续提交？', '提示', {
          confirmButtonText: '继续提交',
          cancelButtonText: '取消',
          type: 'warning'
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    hasMissingFaqPart() {
      return this.parts.some(part => part.faqStatus === 'processing' || !this.hasCompleteFaq(part));
    },
    hasCompleteFaq(part) {
      const faqList = Array.isArray(part.faqList) && part.faqList.length ? part.faqList : parseFaqJson(part.faqJson);
      const completeItems = faqList.filter(item => String(item.question || item.q || '').trim() && String(item.answer || item.a || '').trim());
      return completeItems.length > 0;
    },
    mergeSavedResult(savedParts) {
      if (!savedParts.length) return;
      const normalized = savedParts.map(item => normalizePart(item, this.siteType));
      normalized.forEach(saved => {
        const index = this.parts.findIndex(item => item.clientPartId === saved.clientPartId || item.id === saved.id);
        if (index > -1) this.parts.splice(index, 1, saved);
      });
    },
    scrollToPageTop() {
      this.$nextTick(() => window.scrollTo({ top: 0, left: 0 }));
    }
  }
};
</script>

<style lang="scss" scoped>
.product-case-edit-page {
  min-height: calc(100vh - 96px);
  padding: 10px 0 84px;
  background: #f4f7fb;
  color: #071f43;
}
.product-case-edit-page.is-upload-step {
  height: calc(100vh - 96px);
  min-height: 0;
  box-sizing: border-box;
  padding: 22px 0 0;
  overflow: hidden;
}
.product-case-edit-page.is-upload-step.has-uploaded-images {
  height: auto;
  min-height: calc(100vh - 96px);
  padding-bottom: 48px;
  overflow: visible;
}
.publish-upload-card {
  position: relative;
  width: min(1536px, calc(100% - 32px));
  min-height: 710px;
  margin: 0 auto;
  padding: 34px 62px 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .94);
  box-shadow: none;
}
.product-case-edit-page.is-upload-step .publish-upload-card::after {
  content: none;
}
.product-case-edit-page.is-upload-step .publish-upload-card {
  height: 710px;
  min-height: 710px;
  box-sizing: border-box;
}
.product-case-edit-page.is-upload-step.has-uploaded-images .publish-upload-card {
  height: auto;
  min-height: 710px;
}
.publish-upload-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 24px;
}
.publish-title-block h1 {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  color: #00143d;
  font-size: 32px;
  line-height: 1.12;
  font-weight: 900;
  letter-spacing: -1px;
}
.publish-title-block h1 span {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  height: 44px;
  padding: 0 8px;
  border-radius: 9px;
  background: linear-gradient(135deg, #8069ff 0%, #315dff 100%);
  color: #fff;
  font-size: 32px;
  line-height: 1;
}
.publish-title-block h1 span::after {
  content: '';
  position: absolute;
  right: -7px;
  top: -7px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #30dbc8;
}
.publish-title-block p {
  margin: 0;
  color: #53647f;
  font-size: 14px;
  font-weight: 600;
}
.publish-flow-steps {
  display: flex;
  align-items: center;
  gap: 46px;
  padding: 12px 0 0;
  margin: 0;
  list-style: none;
  white-space: nowrap;
}
.publish-flow-steps li {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00143d;
  font-size: 14px;
  font-weight: 900;
}
.publish-flow-steps li:not(:last-child)::after {
  content: '';
  position: absolute;
  left: calc(100% + 16px);
  top: 50%;
  width: 26px;
  height: 2px;
  transform: translateY(-50%);
  background: #b8caff;
}
.publish-flow-steps b {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 1px solid #c9d7ef;
  border-radius: 50%;
  background: #f5f8ff;
  color: #63738c;
  font-size: 16px;
}
.publish-flow-steps .is-active {
  min-height: 44px;
  padding: 5px 16px 5px 6px;
  border: 1px solid #c9d9ff;
  border-radius: 999px;
  box-shadow: 0 12px 28px rgba(44, 94, 255, .16);
}
.publish-flow-steps .is-active b {
  border-color: #315dff;
  background: #315dff;
  color: #fff;
}
.publish-flow-steps .is-active span {
  color: #135cff;
}
.review-head h1 {
  margin: 0 0 8px;
  color: #071f43;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 900;
}
.review-head p {
  margin: 0;
  color: #53647f;
  font-size: 14px;
}
.review-head {
  width: min(1500px, calc(100% - 60px));
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  padding: 24px 0 22px;
}
.review-flow-steps {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 0;
  margin: 0;
  list-style: none;
  white-space: nowrap;
}
.review-flow-steps li {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6f7e96;
  font-size: 14px;
  font-weight: 800;
}
.review-flow-steps li:not(:last-child)::after {
  content: '';
  width: 14px;
  height: 1px;
  margin-left: 10px;
  background: #bdc8d9;
}
.review-flow-steps b {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  background: #14bf8b;
  font-size: 13px;
  font-weight: 900;
}
.review-flow-steps .is-active {
  min-height: 36px;
  padding: 4px 13px 4px 5px;
  border: 1px solid #c7d7fe;
  border-radius: 999px;
  color: #1d4ed8;
  background: #f7faff;
}
.review-flow-steps .is-active b {
  width: 26px;
  height: 26px;
  background: #2563eb;
}
.review-shell {
  width: min(1500px, calc(100% - 60px));
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
  margin: 0 auto;
}
.part-navigator-slot {
  width: 150px;
  min-height: 1px;
}
.review-panel {
  min-width: 0;
  padding: 22px 24px 120px;
  border: 1px solid #dce6f3;
  border-radius: 8px;
  background: #fff;
  box-shadow: none;
  overflow: hidden;
}
.panel-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: -22px -24px 20px;
  padding: 16px 24px;
  border-bottom: 1px solid #e1e9f4;
  background: #fff;
}
.panel-toolbar > strong {
  color: #071f43;
  font-size: 18px;
  font-weight: 900;
}
.part-processing-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border: 1px solid #cfe0ff;
  border-radius: 8px;
  background: #f3f7ff;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 800;
}
.part-warnings {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border: 1px solid #ffe4ba;
  border-radius: 8px;
  background: #fff8ed;
  color: #b76512;
}
h2 {
  margin: 0 0 14px;
  color: #071f43;
  font-size: 16px;
  font-weight: 900;
}
h2 em {
  color: #e13556;
  font-style: normal;
}
.bottom-actions {
  position: fixed;
  z-index: 10;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 70px;
  padding: 0 66px 0 246px;
  border-top: 1px solid #dfe7f3;
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 -10px 30px rgba(22, 42, 75, .08);
}
.bottom-actions .el-button {
  min-width: 142px;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
}
.bottom-actions .el-button:not(.is-disabled) {
  cursor: pointer;
}
.bottom-actions .el-button--primary {
  min-width: 210px;
  border-color: #2458d6;
  background: #2458d6;
}
@media (max-width: 1360px) {
  .publish-upload-card {
    width: calc(100% - 32px);
    padding: 28px;
  }
  .publish-upload-head {
    flex-direction: column;
  }
}
</style>
